import React from "react";
import { useNavigate } from "react-router-dom";
import { Play, ArrowRight, Heart, Star, CheckCircle } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/welcome-video');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-[2rem] md:text-[2.625rem] font-semibold text-foreground mb-6">
            Preserve Your <span className="text-primary">Legacy</span> Forever
          </h1>
          <p className="text-[20px] font-normal text-muted-foreground max-w-3xl mx-auto mb-10">
            Share your life stories, wisdom, and memories with future generations through 
            professional in-person recording sessions that capture your unique legacy.
          </p>
          
          <button 
            onClick={handleGetStarted}
            className="btn-primary text-lg px-8 py-4 inline-flex items-center"
          >
            Get Started
            <ArrowRight className="ml-2" size={20} />
          </button>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-secondary border-y border-secondary-border">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-[2.625rem] font-semibold text-foreground mb-4">How It Works</h2>
            <p className="text-base font-normal text-muted-foreground">Simple steps to preserve your legacy</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-background border border-secondary-border rounded-xl">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-primary-foreground">1</span>
              </div>
              <h3 className="text-base font-semibold text-foreground mb-4">Book Your Session</h3>
              <p className="text-base font-normal text-muted-foreground">
                Choose a convenient time and location for your in-person recording. 
                We come to you or meet at our professional studio.
              </p>
            </div>
            
            <div className="text-center p-8 bg-background border border-secondary-border rounded-xl">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-primary-foreground">2</span>
              </div>
              <h3 className="text-base font-semibold text-foreground mb-4">Invite Family & Friends</h3>
              <p className="text-base font-normal text-muted-foreground">
                Share with loved ones who can contribute meaningful questions 
                to guide your storytelling session.
              </p>
            </div>
            
            <div className="text-center p-8 bg-background border border-secondary-border rounded-xl">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-primary-foreground">3</span>
              </div>
              <h3 className="text-base font-semibold text-foreground mb-4">Professional Recording</h3>
              <p className="text-base font-normal text-muted-foreground">
                Our skilled interviewer guides you through sharing your stories 
                in a comfortable, conversational setting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-[2.625rem] font-semibold text-foreground mb-4">Why Choose Postscrypt</h2>
            <p className="text-base font-normal text-muted-foreground">Professional legacy preservation with personal touch</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Heart className="text-primary" size={24} />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Personal Touch</h3>
              <p className="text-sm text-muted-foreground">Intimate, comfortable storytelling environment</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Star className="text-primary" size={24} />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">Professional Quality</h3>
              <p className="text-base font-normal text-muted-foreground">High-definition video and crystal-clear audio</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-primary" size={24} />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">Family Involvement</h3>
              <p className="text-base font-normal text-muted-foreground">Questions from loved ones make it meaningful</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Play className="text-primary" size={24} />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">Lifetime Access</h3>
              <p className="text-base font-normal text-muted-foreground">Your stories preserved forever, accessible anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary border-y border-secondary-border">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-[2.625rem] font-semibold mb-6 text-foreground">Ready to Share Your Story?</h2>
          <p className="text-base font-normal mb-8 text-muted-foreground">
            Don&apos;t let your precious memories fade away. Start preserving your legacy today.
          </p>
          <button 
            onClick={handleGetStarted}
            className="bg-primary text-primary-foreground font-semibold py-3 px-8 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Begin Your Legacy Journey
          </button>
        </div>
      </section>
    </div>
  );
} 